import type { ApplicationState, ContactData, QuoteData, PaymentInformationData } from "store/application/applicationSlice";
import type { OfferData, LandingPageData } from "types/OfferData";
import type { ContactInformationValues, PolicyBeneficiaryValues, SecondaryAddresseeValues } from "types/Card";
import type { UsStates } from "state/RootSchema";
import { createDate, toLocalISODateString } from "utils/age";
import { cleanPhone } from "utils/formatPhone";

/**
 * Narrowed, non-null core of an ApplicationState that is guaranteed present
 * after a successful validateSubmissionState() call.
 */
interface CoreValidatedState {
    quoteData: QuoteData;
    contactData: ContactData;
    paymentInformationData: PaymentInformationData;
    contactInformation: ContactInformationValues;
    policyBeneficiary: PolicyBeneficiaryValues;
    secondaryAddressee: SecondaryAddresseeValues | null;
    landingPageData: LandingPageData | null;
    offerData: OfferData | null;
}

/**
 * Validates that the Redux ApplicationState contains the data every submission
 * mapper requires (quote, contact, payment, contactInformation, policyBeneficiary)
 * and, optionally, offerData. Throws with a descriptive message listing every
 * missing field. On success, returns the data narrowed to non-null types so
 * callers avoid repeating non-null assertions.
 *
 * Products may declare extra required contact sections (e.g. DTO's
 * statementOfHealth / coverageReplacement) via requiredContactSections; these are
 * validated in the same pass so a single error lists every missing section.
 * Callers re-read those sections from the returned contactData (asserting
 * non-null is safe once validation has passed).
 *
 * @param state - The application state from the Redux store
 * @param opts - Validation options. Set requireOffer when offerData is mandatory;
 *   list requiredContactSections for product-specific required sections.
 * @returns The validated, non-null core state
 */
export type RequiredContactSection = "statementOfHealth" | "coverageReplacement";

export function validateSubmissionState(
    state: ApplicationState,
    opts: { requireOffer: true; requiredContactSections?: RequiredContactSection[] }
): CoreValidatedState & { offerData: OfferData };
export function validateSubmissionState(
    state: ApplicationState,
    opts?: { requireOffer?: false; requiredContactSections?: RequiredContactSection[] }
): CoreValidatedState;
export function validateSubmissionState(
    state: ApplicationState,
    opts: { requireOffer?: boolean; requiredContactSections?: RequiredContactSection[] } = {}
): CoreValidatedState {
    const { quoteData, contactData, paymentInformationData, offerData, landingPageData } = state;

    // Check for missing top-level required data
    const missingFields: string[] = [];
    if (!quoteData) missingFields.push("quoteData");
    if (!contactData) missingFields.push("contactData");
    if (!paymentInformationData) missingFields.push("paymentInformationData");
    if (opts.requireOffer && !offerData) missingFields.push("offerData");

    if (missingFields.length > 0) {
        throw new Error(`Missing required data for submission: ${missingFields.join(", ")}`);
    }

    // Check for missing contact information sections shared by every product
    const contactInformation = contactData!.contactInformation;
    const policyBeneficiary = contactData!.policyBeneficiary;

    const missingContactFields: string[] = [];
    if (!contactInformation) missingContactFields.push("contactInformation");
    for (const section of opts.requiredContactSections ?? []) {
        if (!contactData![section]) missingContactFields.push(section);
    }
    if (!policyBeneficiary) missingContactFields.push("policyBeneficiary");

    if (missingContactFields.length > 0) {
        throw new Error(`Missing required contact information data: ${missingContactFields.join(", ")}`);
    }

    return {
        quoteData: quoteData!,
        contactData: contactData!,
        paymentInformationData: paymentInformationData!,
        contactInformation: contactInformation!,
        policyBeneficiary: policyBeneficiary!,
        secondaryAddressee: contactData!.secondaryAddressee,
        landingPageData,
        offerData,
    };
}

/**
 * Builds an ISO date string (local, no time component) from the split
 * day/month/year parts collected by the contact-information form.
 *
 * @param dateOfBirth - The split date-of-birth parts as strings
 * @returns The birth date as a local ISO date string
 */
export function buildBirthDate(dateOfBirth: { year: string; month: string; day: string }): string {
    return toLocalISODateString(
        createDate(parseInt(dateOfBirth.year, 10), parseInt(dateOfBirth.month, 10), parseInt(dateOfBirth.day, 10))
    );
}

type BuiltSecondaryAddressee =
    | {
          hasSecondaryAddressee: true;
          secondaryAddressee: {
              firstName: string;
              lastName: string;
              phone: string | undefined;
              address: {
                  addressLine1: string;
                  addressLine2: undefined;
                  city: string;
                  state: UsStates;
                  zipCode: string;
              };
          };
      }
    | { hasSecondaryAddressee: false };

/**
 * Builds the secondary-addressee fragment of a submission body. Every product
 * collects and emits this identically: when present, the addressee details are
 * mapped (with the phone cleaned); otherwise only the boolean flag is emitted.
 *
 * @param secondaryAddressee - The secondary addressee form values (or null)
 * @returns The secondary-addressee fragment to spread into a submission body
 */
export function buildSecondaryAddressee(secondaryAddressee: SecondaryAddresseeValues | null): BuiltSecondaryAddressee {
    if (secondaryAddressee?.hasSecondaryAddressee !== true) {
        return { hasSecondaryAddressee: false };
    }

    return {
        hasSecondaryAddressee: true,
        secondaryAddressee: {
            firstName: secondaryAddressee.firstName!,
            lastName: secondaryAddressee.lastName!,
            phone: secondaryAddressee.phone ? cleanPhone(secondaryAddressee.phone) : undefined,
            address: {
                addressLine1: secondaryAddressee.address!,
                addressLine2: undefined,
                city: secondaryAddressee.city!,
                state: secondaryAddressee.state!,
                zipCode: secondaryAddressee.zipCode!,
            },
        },
    };
}

/**
 * Builds the identity and address fields shared by every product's `applicant`
 * object. Product-specific fields (gender, keyCode, aaaMember, health, tiers,
 * etc.) are added by the caller because they vary in source and defaults.
 *
 * zipCode is passed explicitly because its source differs per product (MLTA and
 * GIWL read it from quoteData; DTO reads it from contactInformation).
 *
 * @param contactInformation - The validated contact-information values
 * @param zipCode - The zip code to use for the applicant address
 * @returns The shared applicant identity/address fields
 */
export function buildApplicantIdentity(contactInformation: ContactInformationValues, zipCode: string) {
    return {
        firstName: contactInformation.firstName,
        lastName: contactInformation.lastName,
        birthDate: buildBirthDate(contactInformation.dateOfBirth),
        phone: cleanPhone(contactInformation.phone),
        phoneType: contactInformation.phoneType,
        email: contactInformation.email,
        address: {
            addressLine1: contactInformation.address,
            addressLine2: undefined,
            city: contactInformation.city,
            state: contactInformation.state,
            zipCode,
            country: "USA",
        },
    };
}

/**
 * Builds the single primary beneficiary (at 100%) emitted by every product.
 * All three forms currently support exactly one beneficiary.
 *
 * @param beneficiary - The validated policy-beneficiary values
 * @returns The primary beneficiary object for the `beneficiaries` array
 */
export function buildPrimaryBeneficiary(beneficiary: PolicyBeneficiaryValues) {
    return {
        firstName: beneficiary.firstName,
        lastName: beneficiary.lastName,
        relationship: beneficiary.relationship,
        percentage: 100,
        beneficiaryType: "primary" as const,
    };
}
